/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { forwardRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  deleteColumnById,
  deleteElementById,
  deleteRowById,
  duplicateElementContentBlock,
  duplicateRowContentBlock,
  saveSelectedItem,
  selectSelectedItem,
} from '../../documentSlice';
import { Box, Tooltip } from '@mui/material';
import { ElementSide } from '../../constants';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import styles from './SelectedItem.module.css';

enum Prefix {
  Row = 'row',
  Column = 'column',
  Item = 'item',
}
interface SelectedItemProps {
  children: string | JSX.Element | JSX.Element[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any; //TODO: specify its either a section or a column element
  isDragging: boolean;
}

const getPrefixFromId = (id: string): Prefix => {
  if (id.startsWith(Prefix.Row)) {
    return Prefix.Row;
  } else if (id.startsWith(Prefix.Column)) {
    return Prefix.Column;
  } else {
    return Prefix.Item;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectedItem = forwardRef<any, SelectedItemProps>(({ children, data, isDragging }, ref) => {
  const dispatch = useAppDispatch();
  const selector = useAppSelector;
  const selectedItem = selector(selectSelectedItem);

  const {
    data: { id: itemId },
    pageId,
    columnId,
    rowId,
  } = data;
  const prefix = getPrefixFromId(itemId);
  const isSameItem = selectedItem?.data?.id == itemId;

  const handleSelectedItem = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(saveSelectedItem({ data }));
  };

  const handleRemoveSelectedItem = () => {
    if (isSameItem) {
      switch (prefix) {
        case Prefix.Column:
          dispatch(deleteColumnById({ pageId, rowId, columnId }));
          break;
        case Prefix.Row:
          dispatch(deleteRowById({ rowId: itemId, pageId }));
          break;
        default:
          dispatch(deleteElementById({ rowId, pageId, columnId, elementId: itemId }));
      }
    }
  };

  const handleDuplicateSelectedItem = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    if (prefix === Prefix.Item) {
      dispatch(
        duplicateElementContentBlock({
          pageId,
          rowId,
          columnId,
          itemId,
        })
      );
    }
    if (prefix === Prefix.Row) {
      dispatch(
        duplicateRowContentBlock({
          pageId,
          side: ElementSide.Bottom,
          type: 'BUTTON',
          rowId: itemId,
        })
      );
    }
  };

  return (
    <div className={`${styles.container} ${isSameItem && styles.selectedContainer}`}>
      {isSameItem && (
        <Box
          className={styles.optionsContainer}
          display='flex'
          flexDirection={'row'}
          justifyContent={'space-between'}
          paddingX='8px'
          width='100%'
          sx={{ opacity: Number(!isDragging) }}
        >
          <div ref={ref} className={styles.containerOption}>
            <DragIndicatorRoundedIcon className={styles.icon} />
            <span>Drag</span>
          </div>
          <Box alignItems='center' display='flex' gap={2}>
            <Tooltip title='Duplicate item' arrow>
              <button className={styles.actionableContainerOption} onClick={handleDuplicateSelectedItem}>
                <ContentCopyRoundedIcon className={styles.icon} />
              </button>
            </Tooltip>
            <Tooltip title='Delete item' arrow>
              <button className={styles.actionableContainerOption} onClick={handleRemoveSelectedItem}>
                <DeleteRoundedIcon className={styles.icon} />
              </button>
            </Tooltip>
          </Box>
        </Box>
      )}
      <div onClick={handleSelectedItem}>{children}</div>
    </div>
  );
});

SelectedItem.displayName = 'SelectedItem';

export default SelectedItem;
